import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, MapPin, ArrowRight, Receipt, Star, Gift } from "@phosphor-icons/react";
import { VERTICAL_MAP } from "@/lib/api";
import { ReviewModal, StarRating } from "@/components/ReviewModal";

const StatusPill = ({ status }) => {
  const map = {
    pending: { c: "text-yellow-400 bg-yellow-400/10", label: "Awaiting payment" },
    reserved: { c: "text-cyan-400 bg-cyan-400/10", label: "Reserved · Pay on arrival" },
    paid: { c: "text-emerald-400 bg-emerald-400/10", label: "Paid · Finding provider" },
    assigned: { c: "text-blue-400 bg-blue-400/10", label: "Assigned" },
    completed: { c: "text-[#D4AF37] bg-[#D4AF37]/10", label: "Completed" },
    cancelled: { c: "text-red-400 bg-red-400/10", label: "Cancelled" },
  };
  const s = map[status] || { c: "text-zinc-400 bg-zinc-800", label: status };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${s.c}`}>{s.label}</span>;
};

export default function Dashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState({}); // booking_id -> review
  const [reviewFor, setReviewFor] = useState(null);

  const load = async () => {
    if (!user) return nav("/login");
    setLoading(true);
    try {
      const res = await api.get("/bookings/me");
      setBookings(res.data);
      // Fetch existing reviews for completed bookings
      const completed = res.data.filter(b => b.status === "completed");
      const results = await Promise.all(
        completed.map(b => api.get(`/reviews/booking/${b.id}`).then(r => [b.id, r.data]).catch(() => [b.id, null]))
      );
      setReviews(Object.fromEntries(results));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="customer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="eyebrow mb-3">Your dashboard</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl text-white">Hello, {user?.full_name?.split(" ")[0]}.</h1>
        </div>
        <Link
          to="/services/transport"
          data-testid="new-booking-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition"
        >
          New booking <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
        {[
          { label: "Total bookings", value: bookings.length },
          { label: "Paid", value: bookings.filter(b => b.payment_status === "paid").length },
          { label: "Assigned", value: bookings.filter(b => b.status === "assigned").length },
          { label: "Completed", value: bookings.filter(b => b.status === "completed").length },
        ].map(s => (
          <div key={s.label} className="rounded-2xl border border-white/10 bg-[#121214] p-6">
            <div className="font-serif-display text-4xl text-white">{s.value}</div>
            <div className="eyebrow mt-2">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="eyebrow mb-6">Bookings history</div>
      {loading ? (
        <div className="text-zinc-400">Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-12 text-center">
          <Receipt size={40} weight="duotone" className="text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No bookings yet.</p>
          <Link to="/services/transport" className="mt-4 inline-block text-[#D4AF37] hover:underline">Book your first service →</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {bookings.map(b => {
            const meta = VERTICAL_MAP[b.vertical];
            const flag = b.region === "liberia" ? "🇱🇷" : "🇺🇸";
            const existingReview = reviews[b.id];
            const giftAgainHref = b.is_gift
              ? `/book/${b.vertical}/${b.lookup_key}?gift=1&r_name=${encodeURIComponent(b.gift_recipient_name || "")}&r_phone=${encodeURIComponent(b.gift_recipient_phone || "")}&r_msg=${encodeURIComponent(b.gift_message || "")}`
              : null;
            return (
              <div key={b.id} className={`rounded-2xl border p-6 flex flex-wrap items-center gap-6 transition ${b.is_gift ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#1a1610] via-[#121214] to-[#1a1815] hover:border-[#D4AF37]" : "border-white/10 bg-[#121214] hover:border-[#D4AF37]/40"}`}>
                <div className="w-1.5 h-12 rounded-full" style={{ background: meta?.color }} />
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif-display text-xl text-white">{b.service_name}</div>
                    {b.is_gift && (
                      <span
                        data-testid={`gift-badge-${b.id}`}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#D4AF37] text-black font-semibold"
                      >
                        <Gift weight="fill" size={9} /> Gift
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest">RideXelite {meta?.name}</div>
                  {b.is_gift && b.gift_recipient_name && (
                    <div className="mt-1 text-[10px] text-zinc-400 tracking-wide">→ {b.gift_recipient_name}</div>
                  )}
                  {b.reservation_ref && (
                    <div className="mt-1 text-[10px] text-[#D4AF37] tracking-widest">REF: {b.reservation_ref}</div>
                  )}
                </div>
                <div className="text-sm text-zinc-400 flex items-center gap-2" title={b.region_label || b.region}>
                  <span>{flag}</span> {b.region_label || (b.region === "liberia" ? "Liberia" : "United States")}
                </div>
                <div className="text-sm text-zinc-400 flex items-center gap-2">
                  <CalendarCheck size={16} />
                  {new Date(b.scheduled_at).toLocaleString()}
                </div>
                <div className="text-sm text-zinc-400 flex items-center gap-2 max-w-xs truncate">
                  <MapPin size={16} /> {b.address}
                </div>
                <div className="font-serif-display text-xl text-white">${b.amount.toFixed(2)}</div>
                <StatusPill status={b.status} />
                <div className="flex flex-col items-end gap-2">
                  {giftAgainHref && (
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/gift/${b.reservation_ref}`}
                        data-testid={`view-gift-card-${b.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition"
                      >
                        View card
                      </Link>
                      <Link
                        to={giftAgainHref}
                        data-testid={`gift-again-${b.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition"
                      >
                        <Gift weight="fill" size={10} /> Gift again
                      </Link>
                    </div>
                  )}
                  {b.status === "completed" && (
                    existingReview ? (
                      <div className="flex items-center gap-1" title={existingReview.comment || ""}>
                        <StarRating value={existingReview.rating} readOnly size={14} testidPrefix={`review-shown-${b.id}`} />
                      </div>
                    ) : (
                      <button
                        onClick={() => setReviewFor(b)}
                        data-testid={`leave-review-${b.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition"
                      >
                        <Star size={12} weight="fill" /> Leave review
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {reviewFor && (
        <ReviewModal
          booking={reviewFor}
          onClose={() => setReviewFor(null)}
          onSubmitted={load}
        />
      )}
    </div>
  );
}
